import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import useInstallPrompt from "@/hooks/useInstallPrompt";
import { TID } from "@/constants/testIds";
import { User, SignOut, Plus, GaugeIcon, Crown, DownloadSimple } from "@phosphor-icons/react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { canInstall, isIOS, hasNativePrompt, promptInstall } = useInstallPrompt();

  const handleInstall = async () => {
    if (hasNativePrompt) { await promptInstall(); return; }
    if (isIOS) alert("To install: tap the Share icon in Safari, then choose ‘Add to Home Screen’.");
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-zinc-950/70 border-b border-zinc-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid={TID.navHome} className="flex items-center gap-2 group">
          <img src="/logo-mark.png" alt="Affiliated Connections logo" width="36" height="36"
            className="h-9 w-9 rounded-lg group-hover:rotate-12 transition-transform" />
          <span className="font-display font-black text-lg sm:text-xl tracking-tighter uppercase">
            <span className="text-[#FF007F]">Affiliated</span><span className="text-[#00FFFF]">Connections</span>
          </span>
        </Link>

        <nav className="flex items-center gap-2 sm:gap-3">
          {canInstall && (
            <button onClick={handleInstall} data-testid="nav-install-app"
              className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors">
              <DownloadSimple size={16} weight="bold" /> <span className="hidden sm:inline">Install</span>
            </button>
          )}
          {user ? (
            <>
              {user.owns_platform ? (
                <>
                  <Link to="/" data-testid="nav-view-site"
                    className="hidden sm:flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 border border-zinc-700 hover:border-[#00FFFF] hover:text-[#00FFFF] transition-colors">
                    View Site
                  </Link>
                  <Link to="/dashboard" data-testid={TID.navDashboard}
                    className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 bg-[#FF007F] text-white border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors">
                    <GaugeIcon size={16} weight="bold" /> <span className="hidden sm:inline">My Dashboard</span>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/buy-platform" data-testid="nav-buy-platform"
                    className="hidden md:flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 border border-[#FFD600] text-[#FFD600] hover:bg-[#FFD600] hover:text-black transition-colors">
                    <Crown size={16} weight="bold" /> Buy Platform
                  </Link>
                  <Link to="/my-links" data-testid={TID.navMyLinks}
                    className="hidden sm:block text-xs font-bold uppercase tracking-widest px-3 py-2 border border-zinc-700 hover:border-[#00FFFF] hover:text-[#00FFFF] transition-colors">
                    My Links
                  </Link>
                  <Link to="/advertise" data-testid="nav-advertise"
                    className="hidden lg:block text-xs font-bold uppercase tracking-widest px-3 py-2 border border-zinc-700 hover:border-[#FFD600] hover:text-[#FFD600] transition-colors">
                    Advertise
                  </Link>
                  <Link to="/submit" data-testid={TID.navSubmit}
                    className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 bg-[#FF007F] text-white border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors">
                    <Plus size={16} weight="bold" /> <span className="hidden sm:inline">Post a Link</span>
                  </Link>
                </>
              )}
              <button onClick={() => { logout(); navigate("/"); }} data-testid={TID.navLogout}
                className="p-2 border border-zinc-700 hover:border-[#FF007F] hover:text-[#FF007F] transition-colors" title="Log out">
                <SignOut size={16} weight="bold" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid={TID.navLogin}
                className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 border border-zinc-700 hover:border-[#00FFFF] hover:text-[#00FFFF] transition-colors">
                <User size={16} weight="bold" /> Login
              </Link>
              <Link to="/register" data-testid={TID.navRegister}
                className="text-xs font-bold uppercase tracking-widest px-3 py-2 bg-[#FF007F] text-white border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors">
                Join
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
